import { PartyTypes } from '../enums';
import { DEFAULT_AUTO_REMOVE_HOURS } from '../constants';
import { keepsBookedSlot } from './keeps-booked-slot';
import { MS_PER_HOUR } from '../../core/constants';
import { getPartyExpectedEnd } from './get-party-expected-end';

export interface PartyOccupancyInput {
  type?: PartyTypes;
  quoted_time: number;
  seated_time?: number | null;
  completedAt?: number | null;
  duration?: number | null;
}

export interface PartyOccupancyOptions {
  /** Current wall-clock time (ms). Active seated parties occupy through this when overtime. */
  now: number;
  /** Fallback dining duration (ms) when the party has no duration set. */
  defaultDuration: number;
  /**
   * Whether the location tracks service after seating (bookingOptions.servingTracking —
   * required for the floor plan). When false the restaurant records nothing after Serve:
   * no completion time, no meaningful seated-occupancy. A party must then not be assumed
   * present beyond its planned window: a reservation occupies [min(quoted_time, seated_time),
   * quoted_time + duration], a seated walk-in [seated_time, seated_time + duration] (its
   * quoted_time is only an estimate). There is no overtime extension through now, a
   * completedAt before the planned end ends the window there (a later one never extends
   * it), and a party with no real (non-0) duration makes no occupancy claim at all (the
   * function returns null; defaultDuration is never used to fabricate presence). Required
   * (no default) so every caller decides explicitly from the location's actual config.
   */
  servingTracking: boolean;
  /**
   * The location's autoRemove window (ms). An uncompleted seated party more than this far
   * past its expected end has aged out of the active-party window (mirrors the autoRemove
   * setting that drops such parties from active lists): its occupancy stops extending
   * through now and ends at the expected end instead. Defaults to the platform
   * DEFAULT_AUTO_REMOVE_HOURS.
   */
  autoRemoveMs?: number;
}

export interface PartyOccupancy {
  start: number;
  end: number;
}

/**
 * Type-aware physical-table occupancy interval for a party.
 *
 * - Reservations occupy their *booked* slot: start = min(quoted_time, seated_time),
 *   end = quoted_time + duration. A late arrival still finishes at the booked end, but one
 *   seated after its slot ended is a new seating and occupies like a walk-in.
 * - Walk-ins / waitlist occupy from when they sit: start = seated_time,
 *   end = seated_time + duration (quoted_time is just the join time and is ignored once seated).
 * - An active (uncompleted) seated party extends through `now` when it runs overtime — until
 *   it ages out of the autoRemove window, after which it ends at its expected end.
 * - A completed party ends at completedAt (with servingTracking off, at the earlier of
 *   completedAt and its planned end).
 * - Returns null when no occupancy can honestly be claimed: servingTracking off and no real
 *   duration recorded (see PartyOccupancyOptions.servingTracking).
 *
 * This is the single source of truth shared by the backend seated-conflict check
 * (CheckAvailableResourceService.findSeatedConflicts / PartyRepository.getSeatedOnResources),
 * the availability engine's usage map (AvailabilityCalculatorService.buildResourceUsageMap),
 * and the day-grid conflict mirror (DayGridViewComponent.findSeatedConflictsOnTables), so
 * they can never disagree about who is at a table.
 */
export function getPartyOccupancy(
  party: PartyOccupancyInput,
  { now, defaultDuration, servingTracking, autoRemoveMs }: PartyOccupancyOptions,
): PartyOccupancy | null {
  // Without servingTracking, overtime doesn't model table occupancy — the most we can
  // claim is the planned window, cut short by a completion, and only when the party has a
  // real duration: fabricating one with defaultDuration would invent presence the
  // restaurant never recorded
  if (!servingTracking) {
    if (!party.duration) {
      return null;
    }

    const seatedTime = party.seated_time ?? null;
    const endsBy = party.completedAt || Infinity;
    if (keepsBookedSlot(party, party.duration)) {
      let start = party.quoted_time;
      if (seatedTime != null) {
        start = Math.min(party.quoted_time, seatedTime);
      }

      return { start, end: Math.min(party.quoted_time + party.duration, endsBy) };
    }

    let start = party.quoted_time;
    if (seatedTime != null) {
      start = seatedTime;
    }

    return { start, end: Math.min(start + party.duration, endsBy) };
  }

  const autoRemove = autoRemoveMs ?? DEFAULT_AUTO_REMOVE_HOURS * MS_PER_HOUR;

  const seated = party.seated_time ?? null;

  // The un-extended end is the shared expected-end rule — the same one the floor-plan UI reads
  // to answer "when does this table free?", so the guard and the UI can't drift apart.
  const endBase = getPartyExpectedEnd(party, defaultDuration);

  let start: number;
  if (seated === null) {
    start = party.quoted_time;
  } else {
    start = keepsBookedSlot(party, party.duration || defaultDuration)
      ? Math.min(party.quoted_time, seated)
      : seated;
  }

  let end: number;
  if (party.completedAt) {
    end = party.completedAt;
  } else if (seated != null) {
    // Active seated party: occupies through now when overtime — unless it has aged out of
    // the autoRemove window (uncompleted but more than autoRemove past its expected end),
    // in which case it stops counting as active and its occupancy ends at the expected end.
    end = now > endBase + autoRemove ? endBase : Math.max(endBase, now);
  } else {
    end = endBase;
  }

  return { start, end };
}
